<template>
    <div class="workspace-shell h-screen-index">
        <div class="workspace-window">
            <header class="workspace-toolbar">
                <nav class="workspace-toolbar__path" :aria-label="$tt('当前文件夹')">
                    <div class="workspace-toolbar__path-main">
                        <button class="workspace-icon-button" type="button" :title="$tt('返回上一级')" :aria-label="$tt('返回上一级')"
                            @click="$refs['file-tree'].backDir()">‹</button>
                        <button class="workspace-root-button" type="button" :title="$tt('返回根目录')"
                            @click="navigateToPath(rootPath)">
                            <svg-icon name="icon-folder" class="workspace-root-button__icon" />
                        </button>
                        <div class="workspace-breadcrumbs" :title="filePath">
                            <button v-for="(segment, index) in pathSegments" :key="segment.path" type="button"
                                class="workspace-breadcrumb" :class="{ 'is-current': index === pathSegments.length - 1 }"
                                @click="navigateToPath(segment.path)">
                                {{ segment.name }}
                            </button>
                        </div>
                    </div>
                    <div class="workspace-toolbar__controls">
                        <div class="workspace-toolbar__selection" aria-live="polite" v-if="selectionSummary">
                            {{ selectionSummary }}
                        </div>
                        <button class="workspace-icon-button workspace-refresh-button" type="button" :title="$tt('刷新文件列表')"
                            :aria-label="$tt('刷新文件列表')" @click="$refs['file-tree'].handleSearchFile(true)">
                            <svg-icon name="refresh" />
                        </button>
                        <div class="workspace-sort dropdown dropdown-end">
                            <button tabindex="0" class="workspace-sort__button" type="button" :title="$tt('排序')"
                                :aria-label="$tt('排序')">
                                <svg class="workspace-sort__icon" viewBox="0 0 16 12" fill="none"
                                    xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                    <path fill-rule="evenodd" clip-rule="evenodd"
                                        d="M0.969727 3.96967L4.93939 8.70228e-06H5.75006V12H4.25006V2.81066L2.03039 5.03033L0.969727 3.96967ZM11.7501 0H10.2501L10.2501 12H11.0607L15.0304 8.03033L13.9697 6.96967L11.7501 9.18934L11.7501 0Z" />
                                </svg>
                                <span v-if="sortSummary" class="workspace-sort__label">{{ sortSummary }}</span>
                            </button>
                            <div tabindex="0" class="workspace-sort__menu dropdown-content z-20">
                                <button v-for="option in sortOptions" :key="option.id" class="workspace-sort__item"
                                    :class="{ 'is-active': getSortStatus(option.id) }" type="button"
                                    @click="changeSorted(option.id)">
                                    <span>{{ $tt(option.label) }}</span>
                                    <span v-if="getSortStatus(option.id) === 'asc'" class="workspace-sort__state">
                                        {{ $tt('升序') }}
                                    </span>
                                    <span v-else-if="getSortStatus(option.id) === 'desc'" class="workspace-sort__state">
                                        {{ $tt('降序') }}
                                    </span>
                                </button>
                                <button v-if="sortedField.length" class="workspace-sort__clear" type="button"
                                    @click="clearSortedFields">
                                    {{ $tt('清除排序') }}
                                </button>
                            </div>
                        </div>
                        <select v-model="activeFilter" class="workspace-filter" :aria-label="$tt('筛选过滤')" :title="$tt('筛选过滤')"
                            @change="handleWorkspaceFilter">
                            <option value="">{{ $tt('筛选') }}</option>
                            <option v-for="option in filterOptions" :key="option.id" :value="option.id">
                                {{ $tt(option.nameKey) }}
                            </option>
                        </select>
                        <div class="workspace-toolbar__search">
                            <input v-model="searchWord" type="search" :placeholder="$tt('搜索文件或文件夹')"
                                :aria-label="$tt('搜索文件或文件夹')" autocomplete="off" @input="handleWorkspaceSearchInput"
                                @focus="showWorkspaceSearchResults = Boolean(searchWord)" @blur="hideWorkspaceSearchResults"
                                @keyup.enter="handleWorkspaceSearch" />
                            <button v-if="searchWord" class="workspace-icon-button workspace-search-clear" type="button"
                                :title="$tt('清除搜索')" :aria-label="$tt('清除搜索')" @click="clearWorkspaceSearch">×</button>
                            <div v-if="showWorkspaceSearchResults && searchWord" class="workspace-search-results">
                                <div v-if="isWorkspaceSearchLoading" class="workspace-search-results__status">
                                    {{ $tt('正在搜索...') }}
                                </div>
                                <template v-else>
                                    <button v-for="suggestion in workspaceSearchSuggestions" :key="suggestion.id"
                                        class="workspace-search-result" type="button" @mousedown.prevent
                                        @click="selectWorkspaceSearchSuggestion(suggestion)">
                                        <svg-icon :name="suggestion.file_type === 'folder' ? 'icon-folder' : 'icon-script-file'"
                                            class="workspace-search-result__icon" />
                                        <span class="workspace-search-result__content">
                                            <span class="workspace-search-result__name">{{ suggestion.name }}</span>
                                            <span class="workspace-search-result__path">{{ suggestion.path }}</span>
                                        </span>
                                    </button>
                                    <div v-if="workspaceSearchSuggestions.length === 0" class="workspace-search-results__status">
                                        {{ $tt('未找到相关结果') }}
                                    </div>
                                </template>
                            </div>
                        </div>
                        <button class="workspace-detail-toggle" type="button" :title="$tt('详情显示设置')" :aria-label="$tt('详情显示设置')"
                            @click="openBatchSettingsDrawer">
                            <svg-icon name="settings" class="workspace-detail-toggle__icon" />
                        </button>
                    </div>
                </nav>
            </header>
            <div class="workspace-content">
                <aside ref="file-section" class="file-section workspace-pane workspace-pane--tree">
                    <div class="workspace-pane__header hidden md:flex">
                        <span>{{ $tt('文件夹') }}</span>
                        <span class="workspace-pane__hint">{{ $tt('选择要处理的内容') }}</span>
                    </div>
                    <div class="workspace-pane__body">
                        <file-tree ref="file-tree" :show-toolbar="false"></file-tree>
                    </div>
                </aside>
                <div class="resize-handle" :class="{ resizing: isResizing }" @mousedown="startResize"></div>
                <div v-show="batchPanelVisible" class="batch-drawer-backdrop" @click="closeBatchPanel"></div>
                <section v-show="batchPanelVisible" ref="edit-section"
                    :class="{ 'is-expanded': batchActionExpanded }"
                    class="edit-section workspace-pane workspace-pane--actions">
                    <div class="workspace-pane__body workspace-pane__body--actions">
                        <transition name="slide-fade">
                            <batch-action ref="batchAction"
                                @close="closeBatchPanel" @detail-change="handleBatchActionDetailChange"></batch-action>
                        </transition>
                    </div>
                </section>
                <main ref="resource-section" class="resource-section workspace-pane workspace-pane--resources">
                    <!-- <div class="workspace-pane__header hidden md:flex">
                        <span>{{ $tt('文件详情') }}</span>
                        <span class="workspace-pane__hint">{{ $tt('检查、筛选并编辑标签') }}</span>
                    </div> -->
                    <div class="workspace-pane__body workspace-pane__body--resources">
                        <!-- <transition name="slide-fade" v-if="!isShowResourceId3">
                    <resource v-show="fadeShowDetail" :song-list="SongList" :section3-loading="section3Loading">
                    </resource>
                </transition> -->
                        <transition name="slide-fade">
                            <div>
                                <div v-if="showId3ListLoading" class="flex justify-center">
                                    <span class="loading loading-infinity loading-lg"></span>
                                </div>
                                <multi-meta-data v-show="showId3List" :file-id3-list="fileID3List"
                                    :files-number="filesNumber" :folder-number="folderNumber" :has-more="hasMoreData"
                                    :is-loading="isLoadingMore" @load-more="handleLoadMore"
                                    @page-size-change="handlePageSizeChange"
                                    @unique-lists-updated="handleUniqueListsUpdated">
                                </multi-meta-data>
                            </div>
                        </transition>
                        <div v-show="!fadeShowDetail && !showTranslation && !isShowResourceId3 && !showId3List"
                            class="workspace-empty-art">
                            <workspace-empty-state :show-artwork="!isHiddenBGP" />
                        </div>
                    </div>
                </main>
            </div>
            <transition name="selection-bar">
                <div v-if="checkedIds.length && !batchPanelVisible" class="selection-action-bar" aria-live="polite">
                    <div class="selection-action-bar__summary">
                        <strong>{{ $tt('已选择 {count} 项', { count: checkedIds.length }) }}</strong>
                        <span>{{ selectionSummary }}</span>
                    </div>
                    <div class="selection-action-bar__commands">
                        <button class="btn btn-primary btn-sm" type="button" @click="openBatchPanel">
                            {{ $tt('批量处理') }}
                        </button>
                        <button class="btn btn-ghost btn-sm" type="button" @click="clearSelection">
                            {{ $tt('取消选择') }}
                        </button>
                    </div>
                </div>
            </transition>
            <!-- 元数据详情 -->
            <div class="drawer">
                <input id="edit" type="checkbox" class="drawer-toggle" v-model="isShowEdit2" />
                <div class="drawer-side">
                    <label for="edit" aria-label="close sidebar" class="drawer-overlay"></label>
                    <div
                        class="p-4 pb-20 md:pb-4 min-h-full bg-base-100 text-base-content w-full md:w-96 pb-20 md:pb-4">
                        <div class="flex justify-between items-center mb-4">
                            <div class="text-xl font-medium">{{ $tt('元数据详情') }}</div>
                            <div>
                                <button class="btn btn-sm" @click="isShowEdit2 = false">
                                    x
                                </button>
                            </div>
                        </div>
                        <div>
                            <music-meta-data v-show="isShowEdit2" :loading='section2Loading' :music-info="musicInfo"
                                :reload-img="reloadImg" :drawer-mode="'drawer'"
                                :unique-artist-options="uniqueArtistOptions" :unique-album-options="uniqueAlbumOptions"
                                :unique-year-options="uniqueYearOptions" :unique-genre-options="uniqueGenreOptions"
                                :unique-artwork-options="uniqueArtworkOptions"
                                @show-version-history="handleShowVersionHistory">
                            </music-meta-data>
                        </div>
                    </div>
                </div>
            </div>
            <!-- 资源详情 -->
            <div class="drawer drawer-end">
                <input id="resource-id3" type="checkbox" class="drawer-toggle" v-model="isShowResourceId3" />
                <div class="drawer-side">
                    <label for="resource-id3" aria-label="close sidebar" class="drawer-overlay"></label>
                    <div class="p-4 min-h-full bg-base-100 text-base-content w-80 md:w-[800px]">
                        <resource :song-list="SongList" :section3-loading="section3Loading"></resource>
                    </div>
                </div>
            </div>
            <!-- 歌词翻译 -->
            <div class="drawer drawer-end">
                <input id="trans-lrc" type="checkbox" class="drawer-toggle" v-model="showTranslation" />
                <div class="drawer-side">
                    <label for="trans-lrc" aria-label="close sidebar" class="drawer-overlay"></label>
                    <div class="p-4 min-h-full bg-base-100 text-base-content w-80 md:w-[800px]">
                        <div style="display: flex;height: 100%;">
                            <button class="btn btn-xs mt-4" @click="handleCopy('lyric_tran', translationText)">
                                <svg-icon name="left-arrow" class="icon w-6 h-6" />
                            </button>
                            <div style="width: 100%;height: 100%;">
                                <textarea placeholder="" v-model="translationText"
                                    class="mt-4 textarea textarea-bordered textarea-xs w-full h-screen-header">
                                </textarea>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <!-- 歌词时间轴 -->
            <div class="drawer drawer-end">
                <input id="lyrics-timeline" type="checkbox" class="drawer-toggle" v-model="showLyricsTimeline" />
                <div class="drawer-side">
                    <label for="lyrics-timeline" aria-label="close sidebar" class="drawer-overlay"></label>
                    <div class="p-4 min-h-full bg-base-100 text-base-content w-full max-w-7xl">
                        <div class="flex justify-between items-center mb-4">
                            <div class="text-xl font-medium"></div>
                            <button class="btn btn-sm btn-ghost" @click="showLyricsTimeline = false">
                                <svg class="w-4 h-4 fill-current" viewBox="0 0 24 24">
                                    <path
                                        d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                                </svg>
                            </button>
                        </div>
                        <lyrics-timeline :play-path="playPath" :raw-lyrics="rawLyrics" @saveLyrics="handleSaveLyrics" />
                    </div>
                </div>
            </div>
            <!-- 版本历史 -->
            <div class="drawer drawer-end">
                <input id="version-history" type="checkbox" class="drawer-toggle" v-model="showVersionHistory" />
                <div class="drawer-side">
                    <label for="version-history" aria-label="close sidebar" class="drawer-overlay"></label>
                    <div class="min-h-full bg-base-100 text-base-content w-full md:w-[900px]">
                        <!-- 抽屉头部 -->
                        <div class="sticky top-0 bg-base-100 z-10 border-b border-base-300 px-6 py-4">
                            <div class="flex justify-between items-center">
                                <div class="flex items-center gap-3">
                                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    </svg>
                                    <h3 class="font-bold text-xl">{{ $tt('元数据版本历史') }}</h3>
                                </div>
                                <button class="btn btn-sm btn-circle btn-ghost"
                                    @click="showVersionHistory = false">✕</button>
                            </div>
                            <!-- 音轨信息 -->
                            <div v-if="versionTrackInfo && versionTrackInfo.title"
                                class="mt-3 p-3 bg-base-200/50 rounded-lg">
                                <div class="flex items-center gap-2">
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3">
                                        </path>
                                    </svg>
                                    <div class="flex-1 min-w-0">
                                        <div class="font-semibold truncate">{{ versionTrackInfo.title }}</div>
                                        <div class="text-sm opacity-70 truncate">{{ versionTrackInfo.path }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 抽屉内容 -->
                        <div class="p-6 overflow-y-auto" style="height: calc(100vh - 200px);">
                            <MetadataVersionHistory ref="versionHistoryComponent" @restored="handleVersionRestored" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import FileTree from './fileTree'
import MusicMetaData from './musicMetaData'
import BatchAction from './batchAction'
import Resource from './resource'
import MultiMetaData from './multiMetaData'
import LyricsTimeline from './lyricsTimeline'
import MetadataVersionHistory from './MetadataVersionHistory.vue'
import WorkspaceEmptyState from '@/components/home/WorkspaceEmptyState.vue'
import { mapGetters, mapState } from 'vuex'
import SvgIcon from '@/components/SvgIcon.vue'
import { splitArtistText } from '@/utils/artistSplitter'

export default {
    components: {
        MusicMetaData,
        FileTree,
        BatchAction,
        Resource,
        MultiMetaData,
        LyricsTimeline,
        MetadataVersionHistory,
        WorkspaceEmptyState,
        SvgIcon
    },
    data() {
        return {
            theme: document.documentElement.getAttribute('data-theme'),
            fullPath: '',
            fileName: '',
            resource: localStorage.getItem('resource') ? localStorage.getItem('resource') : 'netease',
            translationText: '',
            baseMusicInfo: {},
            isSaveLyricsFile: false,
            isSaveAlbumCover: false,
            isImportLyricsFile: false,
            genreInputList: [],
            languageInputList: [],
            albumArtistInputList: [],
            musicInfo: {
            },
            musicInfoManual: {},
            fadeShowDir: false,
            showId3List: false,
            showId3ListLoading: false,
            isLoading: false,
            SongList: [],
            reloadImg: true,
            test: true,
            isSkipTag: true,
            isFolderAlbum: false,
            albumArtistList: [],
            isShowEdit: false,
            isShowEdit2: false,
            selectAutoMode: 'normal',
            sourceList: [],
            fileID3List: [],
            fileID3ListCopy: [],
            // 分页相关数据
            currentPage: 1,
            pageSize: parseInt(localStorage.getItem('pageSize')) || 50,
            totalCount: 0,
            hasMoreData: false,
            isLoadingMore: false,
            lastRequestParams: null,
            modifyList: ['artist', 'album', 'album_img', 'year', 'lyrics'],
            modifyListL: ['title', 'artist', 'album'],
            relaceTextList: [],
            tidyFormData: {
                root_path: '/app/media/',
                dir_list: [{ 'value': 'artist' }, { 'value': 'album' }]
            },
            isOpenDetail: localStorage.getItem('isOpenDetail') ? localStorage.getItem('isOpenDetail') : true,
            batchSettingFormData: {
                'isOpenDetail': localStorage.getItem('isOpenDetail') !== 'false',
                'isDepth': localStorage.getItem('isDepth') === 'true',
                'limit': localStorage.getItem('limit') ? localStorage.getItem('limit') : 100
            },
            sortedListOrder: ['forward', 'reverse'],
            nowSortedOrder: 'none',
            nowSortedKey: '',
            isShowResourceId3: false,
            fadeShowDetail: false,
            showTranslation: false,
            showLyricsTimeline: false,
            section3Loading: false,
            section2Loading: false,
            isResizing: false,
            startX: 0,
            startWidth: 0,
            playPath: '',
            rawLyrics: '',
            showVersionHistory: false,
            versionTrackInfo: null,
            // 从 multiMetaData 收集的去重列表
            uniqueArtistOptions: [],
            uniqueAlbumOptions: [],
            uniqueYearOptions: [],
            uniqueGenreOptions: [],
            uniqueArtworkOptions: [],
            batchPanelOpen: false,
            batchPanelDismissed: false,
            batchActionExpanded: false,
            // todo test
            rootPath: '/app/media',
            // rootPath: '/Users/macbookair/Music/my_music/music',
            activeFilter: '',
            workspaceSearchSuggestions: [],
            showWorkspaceSearchResults: false,
            isWorkspaceSearchLoading: false,
            workspaceSearchTimer: null,
            workspaceSearchRequestId: 0,
            filterOptions: [
                { id: 'state:null', nameKey: '过滤: 未刮削' },
                { id: 'state:success', nameKey: '过滤: 成功' },
                { id: 'state:failed', nameKey: '过滤: 失败' }
            ],
            sortOptions: [
                { id: 'name', label: '名称' },
                { id: 'update_time', label: '修改时间' },
                { id: 'size', label: '大小' },
                { id: 'state', label: '状态' },
                { id: 'folder', label: '目录' }
            ],
        }
    },
    computed: {
        ...mapGetters(['geFullPath', 'sortedField']),
        ...mapState({
            homeAction: state => state.common.homeAction,
            homeActionTimestamp: state => state.common.homeActionTimestamp
        }),
        filePath: {
            get() {
                return this.geFullPath
            },
            set(value) {
                this.$store.commit('setFullPath', value)
            }
        },
        checkedIds: {
            get() {
                return this.$store.state.common.checkedIds
            },
            set(value) {
                this.$store.commit('setCheckedIds', value)
            }
        },
        checkedData: {
            get() {
                return this.$store.state.common.checkedData
            },
            set(value) {
                this.$store.commit('setCheckedData', value)
            }
        },
        searchWord: {
            get() {
                return this.$store.state.common.searchWord
            },
            set(value) {
                this.$store.commit('setSearchWord', value)
            }
        },
        filesNumber: {
            get() {
                return this.checkedData.filter(item => ['icon-script-file', 'icon-script-files', 'icon-strm', 'icon-cue'].indexOf(item.icon) !== -1).length
            }
        },
        folderNumber: {
            get() {
                return this.checkedData.filter(item => item.icon === 'icon-folder').length
            }
        },
        selectionSummary() {
            const parts = []
            if (this.filesNumber) {
                parts.push(this.$tt('文件 {count}', { count: this.filesNumber }))
            }
            if (this.folderNumber) {
                parts.push(this.$tt('文件夹 {count}', { count: this.folderNumber }))
            }
            return parts.length ? parts.join(' / ') : this.$tt('')
        },
        currentFolderName() {
            if (!this.pathSegments.length) {
                return this.$tt('根目录')
            }
            return this.pathSegments[this.pathSegments.length - 1].name
        },
        activeFilterLabel() {
            if (!this.activeFilter) {
                return this.$tt('未启用筛选')
            }

            const option = this.filterOptions.find(item => item.id === this.activeFilter)
            return option ? this.$tt(option.nameKey) : this.activeFilter
        },
        sortSummary() {
            if (!Array.isArray(this.sortedField) || this.sortedField.length === 0) {
                return ''
            }

            return this.sortedField.map((field) => {
                const isDesc = field.startsWith('-')
                const fieldId = isDesc ? field.slice(1) : field
                const option = this.sortOptions.find(item => item.id === fieldId)
                const label = option ? this.$tt(option.label) : fieldId
                return `${label}${isDesc ? this.$tt('降序') : this.$tt('升序')}`
            }).join(' / ')
        },
        batchPanelVisible() {
            return this.batchPanelOpen || (this.checkedIds.length > 0 && !this.batchPanelDismissed)
        },
        pathSegments() {
            if (!this.filePath) {
                return []
            }

            const segments = []
            if (!this.filePath.startsWith(this.rootPath)) {
                let currentPath = ''
                this.filePath.split('/').filter(Boolean).forEach((part) => {
                    currentPath += `/${part}`
                    segments.push({ name: part, path: currentPath })
                })
                return segments
            }

            segments.push({ name: this.$tt('根目录'), path: this.rootPath })
            let currentPath = this.rootPath
            this.filePath.replace(this.rootPath, '').split('/').filter(Boolean).forEach((part) => {
                currentPath += `/${part}`
                segments.push({ name: part, path: currentPath })
            })
            return segments
        },
        isHiddenBGP: {
            get() {
                return window.localStorage.getItem('isHiddenBGP') === 'true'
            },
            set(value) {
                window.localStorage.setItem('isHiddenBGP', value ? 'true' : 'false')
            }
        }
    },
    watch: {
        homeActionTimestamp(newTimestamp, oldTimestamp) {
            // 当时间戳变化时，执行对应的操作
            if (newTimestamp > oldTimestamp && this.homeAction) {
                this.handleHomeAction(this.homeAction)
            }
        },
        checkedIds(newIds, oldIds) {
            const currentLength = newIds ? newIds.length : 0
            const previousLength = oldIds ? oldIds.length : 0

            if (currentLength === 0) {
                this.batchPanelOpen = false
                this.batchPanelDismissed = false
                return
            }

            if (previousLength === 0) {
                this.batchPanelDismissed = false
            }
        }
    },
    created() {
    },
    mounted() {
        document.addEventListener('mousemove', this.handleResize)
        document.addEventListener('mouseup', this.stopResize)
        // 调试：检查是否正确访问了 store 状态
        console.log('home mounted, homeAction:', this.homeAction, 'homeActionTimestamp:', this.homeActionTimestamp)
        console.log('store state:', this.$store.state)
    },
    beforeDestroy() {
        document.removeEventListener('mousemove', this.handleResize)
        document.removeEventListener('mouseup', this.stopResize)
        clearTimeout(this.workspaceSearchTimer)
    },
    methods: {
        // 点击树的节点后展示详情
        showEditPage(parentPath, nodeName) {
            this.isShowEdit = true
            this.showId3List = this.checkedIds.length > 0
            this.musicInfo = this.baseMusicInfo
            this.fileName = nodeName
            this.section2Loading = true
            this.$api.Task.musicId3({ 'file_path': parentPath, 'file_name': nodeName }).then((res) => {
                if (res.result) {
                    this.section2Loading = false
                    this.musicInfo = res.data
                    this.isSaveLyricsFile = false
                    this.isImportLyricsFile = false
                    this.isSaveAlbumCover = false
                    this.genreInputList = this.musicInfo.genre ? [this.musicInfo.genre] : []
                    this.languageInputList = this.musicInfo.language ? [this.musicInfo.language] : []
                    this.albumArtistInputList = this.musicInfo.albumartist ? [this.musicInfo.albumartist] : []
                    this.albumArtistList = this.parseAlbumArtistList(this.musicInfo.artist)
                    this.musicInfo.files1 = [
                        {
                            name: 'cover.png',
                            status: 'done',
                            url: this.musicInfo.artwork
                        }
                    ]
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('修改失败！'),
                        content: res.message,
                        type: 'error',
                        time: 5000,
                    })
                }
            })
        },
        showEditPageDrawer(parentPath, nodeName) {
            this.isShowEdit = false
            // this.showId3List = this.checkedIds.length > 0
            this.musicInfo = this.baseMusicInfo
            this.fileName = nodeName
            this.section2Loading = true
            this.isShowEdit2 = true
            this.$api.Task.musicId3({ 'file_path': parentPath, 'file_name': nodeName }).then((res) => {
                if (res.result) {
                    this.section2Loading = false
                    this.musicInfo = res.data
                    this.isSaveLyricsFile = false
                    this.isImportLyricsFile = false
                    this.isSaveAlbumCover = false
                    this.genreInputList = this.musicInfo.genre ? [this.musicInfo.genre] : []
                    this.languageInputList = this.musicInfo.language ? [this.musicInfo.language] : []
                    this.albumArtistInputList = this.musicInfo.albumartist ? [this.musicInfo.albumartist] : []
                    this.albumArtistList = this.parseAlbumArtistList(this.musicInfo.artist)
                    this.musicInfo.files1 = [
                        {
                            name: 'cover.png',
                            status: 'done',
                            url: this.musicInfo.artwork
                        }
                    ]
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('修改失败！'),
                        content: res.message,
                        type: 'error',
                        time: 5000,
                    })
                }
            })
        },

        // 处理专辑艺术家
        parseAlbumArtistList(artists) {
            if (!artists) {
                return []
            }

            const artistArray = splitArtistText(artists, this.$store.state.common.sysDict || {})
            const displayArtists = artistArray.length ? artistArray : [artists]
            displayArtists.push(artists)

            return [...new Set(displayArtists)].map((item) => {
                return {
                    'id': item,
                    'name': item
                }
            })
        },
        handleCopy(k, v, callback) {
            if (k === 'lyric') {
                this.resource = localStorage.getItem('resource') ? localStorage.getItem('resource') : 'netease'
                const resurce = this.resource !== 'smart_tag' ? this.resource : v.resource
                this.$api.Task.fetchLyric({ 'song_id': v.id, 'resource': resurce }).then((res) => {
                    if (res.result) {
                        if (res.data !== '') {
                            this.musicInfo['lyrics'] = res.data
                            this.handleCopy('composer', this.musicInfo['lyrics'])
                        } else {
                            this.$myMsg.notify({
                                title: this.$tt('未找到歌词！'),
                                type: 'error',
                                time: 2000,
                            })
                        }
                    } else {
                        this.$myMsg.notify({
                            title: this.$tt('未找到歌词！'),
                            type: 'error',
                            time: 2000,
                        })
                    }
                }).finally(() => {
                    if (callback && typeof callback === 'function') {
                        callback()
                    }
                })
            } else if (k === 'composer') {
                this.$api.Task.extractLyric({ 'lyc': v }).then((res) => {
                    if (res.result) {
                        if (res.data !== '') {
                            this.musicInfo['composer'] = res.data.composer
                            this.musicInfo['lyricist'] = res.data.lyricist
                        } else {
                            console.log()
                        }
                    } else {
                        console.log()
                    }
                })
            }
            else if (k === 'album_img') {
                this.musicInfo[k] = ''
                this.musicInfo['artwork'] = v
                this.musicInfo['files1'] = [
                    {
                        name: 'new.png',
                        status: 'done',
                        url: v
                    }
                ]
                this.reloadImg = false
                this.$nextTick(() => {
                    this.reloadImg = true
                })
            } else if (k === 'lyric_tran') {
                this.musicInfo['lyrics'] = v
            } else if (k === 'artist') {
                this.musicInfo['artist_list'] = splitArtistText(v, this.$store.state.common.sysDict || {})
                this.musicInfo[k] = v
            }
            else {
                this.musicInfo[k] = v
            }
        },
        copyAll(item) {
            this.handleCopy('title', item.name)
            this.handleCopy('year', item.year)
            this.handleCopy('lyric', item)
            this.handleCopy('album', item.album)
            this.handleCopy('artist', item.artist)
            this.handleCopy('album_img', item.album_img)
            this.handleCopy('tracknumber', item.tracknumber)
            this.handleCopy('discnumber', item.discnumber)
            this.handleCopy('totaltracks', item.totaltracks)
            this.handleCopy('totaldiscs', item.totaldiscs)
        },
        nodeExpandedOne(node, expanded) {
            console.log(node, expanded)
        },
        handleSearchFile() {
            this.$refs['file-tree'].handleSearchFile()
        },
        // 单个保存音乐信息
        handleClick() {
            // 刷新目录
            if (this.fileName !== this.musicInfo.filename) {
                this.fileName = this.musicInfo.filename
                this.handleSearchFile()
            }
        },
        fetchFileID3FolderList(mode, refresh = false) {
            const path = require('path')
            const fileName = path.basename(this.filePath)
            const directoryName = path.dirname(this.filePath);
            const batchSettingFormData = {
                'isOpenDetail': localStorage.getItem('isOpenDetail') !== 'false',
                'isDepth': localStorage.getItem('isDepth') === 'true',
                'isOpenWebdav': localStorage.getItem('isOpenWebdav') === 'true',
                'isDepthWebdav': localStorage.getItem('isDepthWebdav') === 'true',
                'limit': localStorage.getItem('limit') ? localStorage.getItem('limit') : 100
            }
            if (!batchSettingFormData.isOpenDetail) {
                return
            }
            if (this.filePath.startsWith('/app/webdav/') && !batchSettingFormData.isOpenWebdav) {
                return
            }
            if (this.filePath.startsWith('/Users/macbookair/Music/my_music/music/music_lyc/webdav') && !batchSettingFormData.isOpenWebdav) {
                return
            }
            let depth = 1
            if (batchSettingFormData.isDepth) {
                depth = -1
            }
            this.showTranslation = false
            this.fadeShowDetail = false
            if (this.checkedIds.length === 0 && this.fileID3List.length !== 0) {
                this.fileID3List = []
            }
            // 重置分页状态
            this.currentPage = 1
            this.hasMoreData = false
            this.showId3ListLoading = true

            // 保存请求参数以便加载更多时使用
            this.lastRequestParams = {
                'file_full_path': directoryName,
                'select_data': [{
                    'icon': 'icon-folder',
                    'name': fileName,
                    'title': fileName,
                }],
                'mode': 'all',
                'limit': batchSettingFormData.limit,
                'depth': depth,
                'sorted_fields': this.sortedField,
                'search_word': this.searchWord,
                'refresh': refresh
            }

            this.$api.Task.fileID3List({
                ...this.lastRequestParams,
                'page': this.currentPage,
                'page_size': this.pageSize
            }).then((res) => {
                if (res.result) {
                    this.showId3ListLoading = false
                    // 适配新的分页数据格式
                    const responseData = res.data
                    console.log(this.hasMoreData)

                    this.hasMoreData = responseData.has_more
                    if (responseData.list && responseData.list.length > 0) {
                        this.fileID3List = responseData.list
                        this.totalCount = responseData.total
                        this.showId3List = true
                    } else if (Array.isArray(res.data) && res.data.length > 0) {
                        // 兼容旧格式
                        this.fileID3List = res.data
                        this.showId3List = true
                    }
                } else {
                    if (res.message.status === 401) {
                        return
                    }
                    this.$myMsg.notify({
                        title: this.$tt('查询失败'),
                        content: res.message,
                        type: 'error',
                        time: 2000,
                    })
                }
            })
        },
        removeFileID3List(id) {
            this.fileID3List = this.fileID3List.filter((item) => item.id !== id)
        },
        deleteFileID3List() {
            this.fileID3List = []
            this.currentPage = 1
            this.hasMoreData = false
        },
        // 处理触底加载更多
        handleLoadMore() {
            if (this.isLoadingMore || !this.hasMoreData || !this.lastRequestParams) {
                return
            }

            this.isLoadingMore = true
            this.currentPage += 1

            this.$api.Task.fileID3List({
                ...this.lastRequestParams,
                'page': this.currentPage,
                'page_size': this.pageSize
            }).then((res) => {
                if (res.result) {
                    const responseData = res.data
                    this.hasMoreData = responseData.has_more
                    console.log(222, this.hasMoreData)
                    if (responseData.list && responseData.list.length > 0) {
                        // 追加数据而不是替换
                        this.fileID3List = [...this.fileID3List, ...responseData.list]
                        this.totalCount = responseData.total
                    } else if (Array.isArray(res.data) && res.data.length > 0) {
                        // 兼容旧格式
                        this.fileID3List = [...this.fileID3List, ...res.data]
                    }
                } else {
                    this.$myMsg.notify({
                        title: this.$tt('加载失败'),
                        message: res.message,
                        type: 'error'
                    })
                }
            }).catch((err) => {
                console.error('加载更多失败:', err)
                this.$myMsg.notify({
                    title: this.$tt('加载失败'),
                    message: this.$tt('网络错误'),
                    type: 'error'
                })
            }).finally(() => {
                this.isLoadingMore = false
            })
        },
        // 处理每页显示数量变化
        handlePageSizeChange(newPageSize) {
            this.pageSize = newPageSize
            localStorage.setItem('pageSize', newPageSize)
            // 重新加载第一页数据
            if (this.lastRequestParams) {
                this.currentPage = 1
                this.showId3ListLoading = true

                this.$api.Task.fileID3List({
                    ...this.lastRequestParams,
                    'page': 1,
                    'page_size': newPageSize
                }).then((res) => {
                    if (res.result) {
                        this.showId3ListLoading = false
                        const responseData = res.data
                        this.hasMoreData = responseData.has_more
                        if (responseData.list && responseData.list.length > 0) {
                            this.fileID3List = responseData.list
                            this.totalCount = responseData.total
                        } else if (Array.isArray(res.data) && res.data.length > 0) {
                            this.fileID3List = res.data
                        }
                    } else {
                        this.showId3ListLoading = false
                        this.$myMsg.notify({
                            title: this.$tt('加载失败'),
                            message: res.message,
                            type: 'error'
                        })
                    }
                }).catch((err) => {
                    console.error('切换每页数量失败:', err)
                    this.showId3ListLoading = false
                })
            }
        },
        toggleLock(mode) {
            if (!this.musicInfo.title) {
                this.$myMsg.notify({
                    title: this.$tt('标题不能为空！'),
                    type: 'error',
                    time: 2000,
                })
                return
            }
            console.log(mode)
            if (mode === 'page') {
                this.showTranslation = false
                this.isShowResourceId3 = false
                this.fadeShowDetail = true
            } else {
                this.showTranslation = false
                this.fadeShowDetail = false
                this.isShowResourceId3 = true
            }
            if (this.filePath.endsWith('/')) {
                this.fullPath = this.filePath + this.fileName
            } else {
                this.fullPath = this.filePath + '/' + this.fileName
            }
            this.section3Loading = true
            this.$api.Task.fetchId3Title({
                title: this.musicInfo.title,
                artist: this.musicInfo.artist,
                album: this.musicInfo.album,
                full_path: this.fullPath,
                resource: localStorage.getItem('resource') ? localStorage.getItem('resource') : 'netease'
            }).then((res) => {
                this.SongList = res.data
                this.section3Loading = false
            })
        },
        // 打开单个编辑抽屉
        handleEdit2(item) {
            console.log('open', item)
            this.musicInfo = item
            this.isSaveLyricsFile = false
            this.isImportLyricsFile = false
            this.isSaveAlbumCover = false
            this.genreInputList = this.musicInfo.genre ? [this.musicInfo.genre] : []
            this.languageInputList = this.musicInfo.language ? [this.musicInfo.language] : []
            this.albumArtistInputList = this.musicInfo.albumartist ? [this.musicInfo.albumartist] : []
            this.albumArtistList = this.parseAlbumArtistList(this.musicInfo.artist)
            this.musicInfo.files1 = [
                {
                    name: 'cover.png',
                    status: 'done',
                    url: this.musicInfo.artwork
                }
            ]
            this.fileName = this.musicInfo.filename
            this.isShowEdit2 = true
        },
        translation() {
            if (!this.musicInfo.lyrics) {
                this.$myMsg.notify({
                    title: this.$tt('歌词不能为空！'),
                    type: 'error',
                    time: 2000,
                })
            }
            this.fadeShowDetail = false
            this.isShowResourceId3 = false
            this.showTranslation = true
            this.$api.Task.translationLyc({
                lyc: this.musicInfo.lyrics
            }).then((res) => {
                this.showTranslation = true
                this.translationText = res.data
            })
        },
        showFileSection() {
            this.closeBatchPanel()
            this.$refs['file-section'].style.setProperty('display', 'block', 'important')
            this.$refs['resource-section'].style.display = 'none'
        },
        showEditSection() {
            this.openBatchPanel()
        },
        showResourceSection() {
            this.closeBatchPanel()
            this.$refs['file-section'].style.display = 'none'
            this.$refs['resource-section'].style.setProperty('display', 'block', 'important')
        },
        startResize(e) {
            this.isResizing = true
            this.startX = e.clientX
            this.startWidth = this.$refs['file-section'].offsetWidth
        },
        handleResize(e) {
            if (!this.isResizing) return

            const width = this.startWidth + (e.clientX - this.startX)
            // 设置最小和最大宽度限制
            if (width >= 200 && width <= window.innerWidth * 0.8) {
                this.$refs['file-section'].style.width = `${width}px`
            }
        },
        stopResize() {
            this.isResizing = false
        },
        navigateToPath(path) {
            this.$refs['file-tree'].navigateToPath(path)
        },
        handleWorkspaceSearch() {
            this.activeFilter = ''
            this.showWorkspaceSearchResults = false
            this.$refs['file-tree'].handleSearch()
        },
        clearWorkspaceSearch() {
            this.searchWord = ''
            this.activeFilter = ''
            this.workspaceSearchSuggestions = []
            this.showWorkspaceSearchResults = false
            this.isWorkspaceSearchLoading = false
            clearTimeout(this.workspaceSearchTimer)
            this.workspaceSearchRequestId += 1
            this.$refs['file-tree'].handleSearch()
        },
        handleWorkspaceSearchInput() {
            clearTimeout(this.workspaceSearchTimer)
            const requestId = ++this.workspaceSearchRequestId
            this.showWorkspaceSearchResults = Boolean(this.searchWord)
            this.workspaceSearchSuggestions = []

            if (!this.searchWord) {
                this.isWorkspaceSearchLoading = false
                return
            }

            this.isWorkspaceSearchLoading = true
            const searchText = this.searchWord
            this.workspaceSearchTimer = setTimeout(() => {
                this.$api.Task.searchIndex({ search_text: searchText }).then((res) => {
                    if (requestId !== this.workspaceSearchRequestId || searchText !== this.searchWord) {
                        return
                    }

                    this.workspaceSearchSuggestions = res.result && Array.isArray(res.data)
                        ? res.data.map(item => ({
                            id: item.id,
                            name: item.name,
                            path: String(item.id || '').replace(/^path:/, ''),
                            file_type: item.file_type || 'music'
                        }))
                        : []
                }).catch(() => {
                    if (requestId === this.workspaceSearchRequestId) {
                        this.workspaceSearchSuggestions = []
                    }
                }).finally(() => {
                    if (requestId === this.workspaceSearchRequestId) {
                        this.isWorkspaceSearchLoading = false
                    }
                })
            }, 300)
        },
        selectWorkspaceSearchSuggestion(suggestion) {
            this.workspaceSearchRequestId += 1
            this.searchWord = suggestion.name
            this.activeFilter = ''
            this.showWorkspaceSearchResults = false
            this.workspaceSearchSuggestions = []
            this.$refs['file-tree'].navigateToPath(suggestion.path)
        },
        hideWorkspaceSearchResults() {
            this.showWorkspaceSearchResults = false
        },
        handleWorkspaceFilter() {
            this.searchWord = this.activeFilter
            if (this.activeFilter) {
                this.$refs['file-tree'].handleSearchFileWithFilter(this.activeFilter)
            } else {
                this.$refs['file-tree'].handleSearchFile()
            }
        },
        changeSorted(fieldId) {
            if (!fieldId) {
                return
            }

            const positiveField = fieldId
            const negativeField = `-${fieldId}`
            const nextSortedFields = Array.isArray(this.sortedField) ? [...this.sortedField] : []
            const positiveIndex = nextSortedFields.indexOf(positiveField)
            const negativeIndex = nextSortedFields.indexOf(negativeField)

            if (positiveIndex > -1) {
                nextSortedFields.splice(positiveIndex, 1)
                nextSortedFields.push(negativeField)
            } else if (negativeIndex > -1) {
                nextSortedFields.splice(negativeIndex, 1)
            } else {
                nextSortedFields.push(positiveField)
            }

            this.$store.commit('setSortedField', nextSortedFields)
            this.refreshWorkspaceAfterSort()
        },
        getSortStatus(fieldId) {
            const positiveField = fieldId
            const negativeField = `-${fieldId}`

            if (this.sortedField.includes(positiveField)) {
                return 'asc'
            }
            if (this.sortedField.includes(negativeField)) {
                return 'desc'
            }
            return null
        },
        clearSortedFields() {
            this.$store.commit('setSortedField', [])
            this.refreshWorkspaceAfterSort()
        },
        openBatchSettingsDrawer() {
            this.batchPanelOpen = true
            this.batchPanelDismissed = false
            this.$nextTick(() => {
                if (this.$refs.batchAction && this.$refs.batchAction.openSettingsDrawer) {
                    this.$refs.batchAction.openSettingsDrawer()
                }
            })
        },
        handleBatchActionDetailChange(isExpanded) {
            this.batchActionExpanded = isExpanded
        },
        refreshWorkspaceAfterSort() {
            this.$nextTick(() => {
                if (this.activeFilter) {
                    this.$refs['file-tree'].handleSearchFileWithFilter(this.activeFilter)
                    return
                }
                this.$refs['file-tree'].handleSearchFile()
            })
        },
        openBatchPanel() {
            this.batchPanelOpen = true
            this.batchPanelDismissed = false
        },
        closeBatchPanel() {
            this.batchPanelOpen = false
            if (this.checkedIds.length > 0) {
                this.batchPanelDismissed = true
            }
        },
        clearSelection() {
            this.checkedIds = []
            this.checkedData = []
            this.closeBatchPanel()
        },
        showLyricsTimelineModal(playPath, lyrics) {
            this.showLyricsTimeline = true
            this.playPath = playPath
            this.rawLyrics = lyrics
        },
        handleSaveLyrics(lyrics) {
            this.musicInfo.lyrics = lyrics
        },
        // 处理移动端导航栏的操作事件
        handleHomeAction(actionName) {
            if (this[actionName] && typeof this[actionName] === 'function') {
                this[actionName]()
            }
        },
        // 处理显示版本历史
        handleShowVersionHistory(trackInfo) {
            this.versionTrackInfo = trackInfo
            this.showVersionHistory = true

            // 等待抽屉打开后再加载版本历史
            this.$nextTick(() => {
                if (this.$refs.versionHistoryComponent) {
                    this.$refs.versionHistoryComponent.open(
                        trackInfo.id,
                        trackInfo.title,
                        trackInfo.path
                    )
                }
            })
        },
        // 处理版本回退成功
        handleVersionRestored(version) {
            this.$myMsg.notify({
                title: this.$tt('版本回退成功'),
                content: this.$tt('已回退到版本 v{version}', { version: version.version_number }),
                type: 'success',
                time: 2000,
            })

            // 关闭抽屉
            this.showVersionHistory = false

            // 刷新当前页面数据
            this.handleClick()
        },
        // 处理从 multiMetaData 接收的去重列表
        handleUniqueListsUpdated(data) {
            this.uniqueArtistOptions = data.artists || []
            this.uniqueAlbumOptions = data.albums || []
            this.uniqueYearOptions = data.years || []
            this.uniqueGenreOptions = data.genres || []
            this.uniqueArtworkOptions = data.artworks || []
        }
    }
}
</script>
<style lang="postcss">
.music-item {
    cursor: pointer;
}

.music-item:hover {
    color: #1facdd;
}

.label1 {
    width: 80px;
}

.parent {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    grid-template-rows: repeat(1, 1fr);
    grid-column-gap: 0;
    grid-row-gap: 0;
    place-items: center;
    margin-bottom: 15px;
}

.title2 {
    font-weight: 500;
}

.song-card {
    display: flex;
    align-items: center;
    border-bottom: 1px solid #E2E2E2;
}

.song-card:hover {
    background: #E2E2E2;
}

.add-button {
    width: 24px;
    height: 24px;
    line-height: 20px;
    display: inline-block;
    background-color: transparent;
    border: 1px solid #ccc;
    border-radius: 5px;
    margin-left: 5px;
    font-size: 12px;
    color: rgb(97, 97, 97);
    text-align: center;
    cursor: pointer;
}

.delete-button {
    width: 24px;
    height: 24px;
    line-height: 20px;
    display: inline-block;
    background-color: transparent;
    border: 1px solid #ccc;
    border-radius: 5px;
    margin-left: 5px;
    font-size: 12px;
    color: rgb(63, 63, 63);
    text-align: center;
    cursor: pointer;
}

.workspace-shell {
    min-height: 0;
    padding: 8px;
    background:
        linear-gradient(135deg, oklch(var(--b2) / 0.92), oklch(var(--b1) / 0.72) 52%, oklch(var(--p) / 0.06)),
        oklch(var(--b2) / 0.56);
}

.workspace-window {
    position: relative;
    display: flex;
    height: 100%;
    min-height: 0;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 12px;
    background: linear-gradient(155deg, oklch(var(--b1)), oklch(var(--b2) / 0.32));
    box-shadow: 0 12px 28px oklch(var(--bc) / 0.08);
}

.workspace-toolbar {
    display: flex;
    min-height: 56px;
    flex: 0 0 56px;
    align-items: center;
    gap: 12px;
    padding: 0 14px;
    border-bottom: 1px solid oklch(var(--bc) / 0.08);
    background: linear-gradient(100deg, oklch(var(--b1)), oklch(var(--p) / 0.035), oklch(var(--b1)));
}

.workspace-toolbar__title,
.workspace-pane__header {
    display: flex;
    align-items: center;
}

.workspace-toolbar__title {
    min-width: 0;
    gap: 9px;
    flex: 0 0 auto;
}

.workspace-toolbar__icon {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
    fill: oklch(var(--p));
}

.workspace-toolbar h1 {
    margin: 0;
    overflow: hidden;
    color: oklch(var(--bc));
    font-size: 13px;
    font-weight: 650;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workspace-toolbar__path {
    display: flex;
    min-width: 0;
    align-items: center;
    flex: 1 1 auto;
    gap: 2px;
}

.workspace-toolbar__path-main {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    align-items: center;
    gap: 2px;
}

.workspace-toolbar__controls {
    display: flex;
    min-width: 0;
    margin-left: auto;
    align-items: center;
    gap: 8px;
}

.workspace-icon-button,
.workspace-root-button,
.workspace-breadcrumb {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border: 0;
    color: oklch(var(--bc) / 0.62);
    background: transparent;
    cursor: pointer;
    transition: color 160ms ease, background-color 160ms ease, transform 160ms ease;
}

.workspace-icon-button,
.workspace-root-button {
    width: 30px;
    height: 30px;
    flex: 0 0 auto;
    border-radius: 8px;
    font-size: 22px;
    line-height: 1;
}

.workspace-icon-button:hover,
.workspace-root-button:hover,
.workspace-breadcrumb:hover {
    color: oklch(var(--p));
    background: oklch(var(--p) / 0.1);
}

.workspace-icon-button:focus-visible,
.workspace-root-button:focus-visible,
.workspace-breadcrumb:focus-visible {
    outline: 2px solid oklch(var(--p) / 0.58);
    outline-offset: 2px;
}

.workspace-icon-button:active,
.workspace-root-button:active,
.workspace-breadcrumb:active {
    transform: scale(0.96);
}

.workspace-root-button__icon {
    width: 15px;
    height: 15px;
    fill: currentColor;
}

.workspace-breadcrumbs {
    display: flex;
    min-width: 0;
    align-items: center;
    overflow-x: auto;
    scrollbar-width: none;
}

.workspace-breadcrumbs::-webkit-scrollbar {
    display: none;
}

.workspace-breadcrumb {
    position: relative;
    max-width: 160px;
    min-height: 30px;
    padding: 0 7px;
    overflow: hidden;
    font-size: 12px;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workspace-breadcrumb+.workspace-breadcrumb::before {
    margin-right: 8px;
    color: oklch(var(--bc) / 0.28);
    content: '/';
}

.workspace-breadcrumb.is-current {
    color: oklch(var(--bc));
    font-weight: 650;
}

.workspace-toolbar__search {
    position: relative;
    display: flex;
    width: clamp(180px, 19vw, 300px);
    height: 32px;
    flex: 0 1 300px;
    align-items: center;
    border: 1px solid oklch(var(--bc) / 0.15);
    border-radius: 8px;
    background: oklch(var(--b2) / 0.58);
    transition: border-color 160ms ease, box-shadow 160ms ease;
}

.workspace-toolbar__search:focus-within {
    border-color: oklch(var(--p) / 0.7);
    box-shadow: 0 0 0 3px oklch(var(--p) / 0.13);
}

.workspace-toolbar__search input {
    width: 100%;
    height: 100%;
    min-width: 0;
    padding: 0 30px 0 10px;
    border: 0;
    outline: 0;
    color: oklch(var(--bc));
    background: transparent;
    font-size: 12px;
}

.workspace-toolbar__search input::placeholder {
    color: oklch(var(--bc) / 0.46);
}

.workspace-search-clear {
    position: absolute;
    right: 1px;
    width: 28px;
    height: 28px;
    font-size: 18px;
}

.workspace-search-results {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    left: 0;
    z-index: 30;
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid oklch(var(--bc) / 0.15);
    border-radius: 8px;
    background: oklch(var(--b1));
    box-shadow: 0 12px 24px oklch(0% 0 0 / 0.16);
}

.workspace-search-result {
    display: flex;
    width: 100%;
    min-height: 48px;
    align-items: center;
    gap: 9px;
    padding: 7px 10px;
    color: oklch(var(--bc));
    text-align: left;
}

.workspace-search-result:hover,
.workspace-search-result:focus {
    background: oklch(var(--p) / 0.1);
    outline: 0;
}

.workspace-search-result__icon {
    width: 16px;
    height: 16px;
    flex: 0 0 auto;
    color: oklch(var(--p));
}

.workspace-search-result__content {
    display: flex;
    min-width: 0;
    flex: 1;
    flex-direction: column;
    gap: 2px;
}

.workspace-search-result__name,
.workspace-search-result__path {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workspace-search-result__name {
    font-size: 12px;
    font-weight: 600;
}

.workspace-search-result__path,
.workspace-search-results__status {
    color: oklch(var(--bc) / 0.55);
    font-size: 11px;
}

.workspace-search-results__status {
    padding: 12px 10px;
    text-align: center;
}

.workspace-detail-toggle {
    display: inline-flex;
    height: 32px;
    flex: 0 0 auto;
    align-items: center;
    gap: 5px;
    padding: 0 9px;
    border: 1px solid oklch(var(--bc) / 0.15);
    border-radius: 8px;
    color: oklch(var(--bc) / 0.68);
    background: oklch(var(--b2) / 0.58);
    font-size: 12px;
    cursor: pointer;
    transition: border-color 160ms ease, color 160ms ease, background-color 160ms ease;
}

.workspace-detail-toggle:hover,
.workspace-detail-toggle:focus {
    border-color: oklch(var(--p) / 0.7);
    color: oklch(var(--p));
    outline: 0;
}

.workspace-detail-toggle__icon {
    width: 15px;
    height: 15px;
    flex: 0 0 auto;
}

.workspace-refresh-button svg {
    width: 15px;
    height: 15px;
    fill: currentColor;
}

.workspace-sort {
    flex: 0 0 auto;
}

.workspace-sort__button {
    display: inline-flex;
    height: 32px;
    max-width: 180px;
    align-items: center;
    gap: 6px;
    padding: 0 9px;
    border: 1px solid oklch(var(--bc) / 0.15);
    border-radius: 8px;
    color: oklch(var(--bc) / 0.7);
    background: oklch(var(--b2) / 0.58);
    font-size: 12px;
    cursor: pointer;
    transition: border-color 160ms ease, box-shadow 160ms ease, color 160ms ease;
}

.workspace-sort__button:hover,
.workspace-sort__button:focus {
    border-color: oklch(var(--p) / 0.7);
    color: oklch(var(--p));
    box-shadow: 0 0 0 3px oklch(var(--p) / 0.13);
    outline: 0;
}

.workspace-sort__icon {
    width: 14px;
    height: 14px;
    flex: 0 0 auto;
    fill: currentColor;
}

.workspace-sort__label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workspace-sort__menu {
    width: 220px;
    margin-top: 8px;
    padding: 7px;
    border: 1px solid oklch(var(--bc) / 0.12);
    border-radius: 12px;
    background: oklch(var(--b1));
    box-shadow: 0 16px 34px oklch(var(--bc) / 0.14);
}

.workspace-sort__item,
.workspace-sort__clear {
    display: flex;
    width: 100%;
    min-height: 32px;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    padding: 0 9px;
    border: 0;
    border-radius: 8px;
    color: oklch(var(--bc) / 0.78);
    background: transparent;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
}

.workspace-sort__item:hover,
.workspace-sort__clear:hover {
    color: oklch(var(--p));
    background: oklch(var(--p) / 0.1);
}

.workspace-sort__item.is-active {
    color: oklch(var(--p));
    background: oklch(var(--p) / 0.12);
    font-weight: 650;
}

.workspace-sort__state {
    flex: 0 0 auto;
    color: currentColor;
    font-size: 11px;
}

.workspace-sort__clear {
    margin-top: 4px;
    color: oklch(var(--bc) / 0.56);
}

.workspace-toolbar__selection {
    flex: 0 0 auto;
    color: oklch(var(--bc) / 0.6);
    font-size: 12px;
    white-space: nowrap;
}

.workspace-filter {
    width: 86px;
    height: 32px;
    flex: 0 0 86px;
    padding: 0 24px 0 9px;
    border: 1px solid oklch(var(--bc) / 0.15);
    border-radius: 8px;
    outline: 0;
    color: oklch(var(--bc) / 0.72);
    background: oklch(var(--b2) / 0.58);
    font-size: 12px;
    cursor: pointer;
}

.workspace-filter:focus {
    border-color: oklch(var(--p) / 0.7);
    box-shadow: 0 0 0 3px oklch(var(--p) / 0.13);
}

.workspace-content {
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 0;
    flex: 1;
    background: linear-gradient(145deg, oklch(var(--b2) / 0.58), oklch(var(--b1) / 0.38));
}

.workspace-pane {
    display: flex;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    background: oklch(var(--b1));
}

.workspace-pane__header {
    height: 38px;
    flex: 0 0 38px;
    justify-content: space-between;
    padding: 0 14px;
    border-bottom: 1px solid oklch(var(--bc) / 0.09);
    color: oklch(var(--bc) / 0.76);
    font-size: 12px;
    font-weight: 600;
}

.workspace-pane__hint {
    overflow: hidden;
    color: oklch(var(--bc) / 0.42);
    font-size: 11px;
    font-weight: 400;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workspace-pane__body {
    min-height: 0;
    flex: 1;
}

.workspace-pane--tree {
    width: 300px;
    flex: 0 0 auto;
    background: oklch(var(--b2) / 0.7);
}

.workspace-pane--actions {
    position: absolute;
    /* Keep nested batch-action drawers above the global navigation header. */
    z-index: 81;
    left: 16px;
    right: 16px;
    bottom: 16px;
    width: min(760px, calc(100% - 32px));
    height: fit-content;
    max-height: calc(100% - 32px);
    overflow: visible;
    margin: 0 auto;
    border: 1px solid oklch(var(--bc) / 0.1);
    border-radius: 12px;
    background: oklch(var(--b1));
    box-shadow: 0 16px 34px oklch(var(--bc) / 0.14);
    transition: width 200ms ease;
}

.workspace-pane__body--actions {
    flex: 0 0 auto;
    min-height: 0;
    overflow: visible;
    padding: 6px 6px 5px;
}

.workspace-pane--actions .workspace-pane__header {
    height: 32px;
    flex-basis: 32px;
}

.workspace-pane--actions .workspace-pane__header .workspace-icon-button {
    cursor: pointer;
}

.workspace-pane--resources {
    flex: 1 1 520px;
    border-left: 1px solid oklch(var(--bc) / 0.08);
}

.workspace-pane__body--resources {
    position: relative;
}

.workspace-empty-art {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    padding: clamp(20px, 4vw, 52px);
    pointer-events: none;
}

.batch-drawer-backdrop {
    position: absolute;
    display: none;
    z-index: 2;
    inset: 56px 0 0;
    background: oklch(var(--bc) / 0.16);
}

.selection-action-bar {
    position: absolute;
    z-index: 4;
    bottom: 18px;
    left: 50%;
    display: flex;
    min-height: 48px;
    max-width: calc(100% - 32px);
    align-items: center;
    gap: 20px;
    padding: 7px 8px 7px 14px;
    border: 1px solid oklch(var(--p) / 0.24);
    border-radius: 12px;
    color: oklch(var(--pc));
    background: oklch(var(--p));
    box-shadow: 0 16px 34px oklch(var(--bc) / 0.16);
    transform: translateX(-50%);
}

.selection-action-bar__summary {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 1px;
    font-size: 12px;
    line-height: 1.25;
}

.selection-action-bar__summary strong {
    font-size: 13px;
}

.selection-action-bar__summary span {
    overflow: hidden;
    opacity: 0.76;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.selection-action-bar__commands {
    display: flex;
    flex: 0 0 auto;
    gap: 4px;
}

.selection-action-bar .btn-primary {
    border-color: oklch(var(--pc) / 0.24);
    color: oklch(var(--p));
    background: oklch(var(--pc));
}

.selection-action-bar .btn-ghost {
    color: oklch(var(--pc));
}

.selection-bar-enter-active,
.selection-bar-leave-active {
    transition: opacity 180ms ease, transform 180ms ease;
}

.selection-bar-enter,
.selection-bar-leave-to {
    opacity: 0;
    transform: translate(-50%, 10px);
}


/*.success {*/
/*    color: #d1cfc5;*/
/*}*/

.failed {
    color: #ac354b;
}

.null {
    color: #333146;
}


.workspace-pane__body::-webkit-scrollbar {
    width: 8px;
    height: 8px;
}

.workspace-pane__body::-webkit-scrollbar-thumb {
    border: 2px solid transparent;
    border-radius: 8px;
    background: oklch(var(--bc) / 0.2);
    background-clip: padding-box;
}

.isSelected {
    background-color: #ecf3fe;
}

.edit-item {
    display: flex;
    margin-bottom: 10px;
    align-items: center;
}

.can-copy {
    cursor: pointer;
}

.workspace-shell {
    min-height: 0;
    background:
        radial-gradient(circle at 14% 12%, oklch(var(--p) / 0.08), transparent 22rem),
        radial-gradient(circle at 90% 8%, oklch(var(--a) / 0.06), transparent 18rem),
        oklch(var(--b2));
}

.workspace-window {
    position: relative;
    display: flex;
    min-height: 0;
    height: 100%;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid oklch(var(--bc) / 0.09);
    border-radius: 16px;
    background: linear-gradient(180deg, oklch(var(--b1) / 0.98), oklch(var(--b2) / 0.74));
    box-shadow:
        0 18px 48px oklch(var(--bc) / 0.08),
        inset 0 1px 0 oklch(var(--b1) / 0.55);
}

.workspace-hero {
    display: grid;
    grid-template-columns: minmax(0, 1.45fr) minmax(260px, 0.95fr);
    gap: 14px;
    padding: 18px;
    border-bottom: 1px solid oklch(var(--bc) / 0.08);
    background:
        radial-gradient(circle at 88% 18%, oklch(var(--p) / 0.08), transparent 18rem),
        linear-gradient(110deg, oklch(var(--b1) / 0.98), oklch(var(--b2) / 0.78));
}

.workspace-hero__copy {
    display: flex;
    min-width: 0;
    flex-direction: column;
    justify-content: center;
    padding: 4px 2px;
}

.workspace-hero__label {
    width: fit-content;
    margin-bottom: 12px;
    padding: 5px 10px;
    border-radius: 999px;
    color: oklch(var(--p));
    background: oklch(var(--p) / 0.12);
    font-size: 12px;
    font-weight: 700;
}

.workspace-hero h1 {
    max-width: 18ch;
    margin: 0;
    color: oklch(var(--bc));
    font-size: clamp(28px, 3.4vw, 44px);
    font-weight: 800;
    line-height: 1.08;
    letter-spacing: 0;
}

.workspace-hero p {
    max-width: 58ch;
    margin: 12px 0 0;
    color: oklch(var(--bc) / 0.62);
    font-size: 14px;
    line-height: 1.75;
}

.workspace-hero__stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
}

.workspace-hero__stat {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 6px;
    padding: 14px;
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 16px;
    background: color-mix(in oklch, oklch(var(--b1)) 90%, transparent);
    box-shadow: inset 0 1px 0 oklch(var(--b1) / 0.45);
}

.workspace-hero__stat span {
    color: oklch(var(--bc) / 0.56);
    font-size: 12px;
}

.workspace-hero__stat strong {
    overflow: hidden;
    color: oklch(var(--bc));
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    font-weight: 700;
}

.workspace-hero__stat small {
    overflow: hidden;
    color: oklch(var(--bc) / 0.5);
    font-size: 11px;
    line-height: 1.4;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.workspace-toolbar {
    position: sticky;
    top: 0;
    z-index: 5;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    border-bottom: 1px solid oklch(var(--bc) / 0.08);
    background: color-mix(in oklch, oklch(var(--b1)) 92%, transparent);
    backdrop-filter: blur(16px);
}

.workspace-toolbar__path {
    width: 100%;
}

.workspace-toolbar__path-main {
    display: flex;
    min-width: 0;
    flex: 1 1 auto;
    align-items: center;
    gap: 2px;
}

.workspace-toolbar__controls {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    flex-wrap: nowrap;
}

.workspace-toolbar__selection {
    display: inline-flex;
    min-height: 34px;
    align-items: center;
    padding: 0 12px;
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 999px;
    color: oklch(var(--bc) / 0.62);
    background: oklch(var(--b2) / 0.58);
    font-size: 12px;
    white-space: nowrap;
}

.workspace-icon-button,
.workspace-root-button,
.workspace-breadcrumb {
    border: 1px solid transparent;
    border-radius: 12px;
    background: transparent;
}

.workspace-icon-button,
.workspace-root-button {
    width: 34px;
    height: 34px;
    color: oklch(var(--bc) / 0.68);
    background: oklch(var(--b2) / 0.58);
}

.workspace-icon-button:hover,
.workspace-root-button:hover,
.workspace-breadcrumb:hover,
.workspace-detail-toggle:hover,
.workspace-sort__button:hover,
.workspace-filter:hover {
    color: oklch(var(--p));
    background: oklch(var(--p) / 0.1);
}

.workspace-icon-button:focus-visible,
.workspace-root-button:focus-visible,
.workspace-breadcrumb:focus-visible,
.workspace-detail-toggle:focus-visible,
.workspace-sort__button:focus-visible,
.workspace-filter:focus-visible,
.workspace-search-clear:focus-visible {
    outline: 2px solid oklch(var(--p) / 0.55);
    outline-offset: 2px;
}

.workspace-breadcrumb {
    min-height: 34px;
    padding: 0 10px;
    color: oklch(var(--bc) / 0.72);
    background: oklch(var(--b2) / 0.36);
    font-size: 12px;
}

.workspace-breadcrumb.is-current {
    color: oklch(var(--bc));
    background: oklch(var(--b2) / 0.72);
    font-weight: 700;
}

.workspace-toolbar__search {
    width: clamp(180px, 22vw, 320px);
    height: 34px;
    background: oklch(var(--b2) / 0.58);
    border-radius: 12px;
}

.workspace-toolbar__search input {
    font-size: 12px;
}

.workspace-search-clear {
    width: 30px;
    height: 30px;
    border-radius: 10px;
}

.workspace-search-results {
    border-radius: 16px;
    border-color: oklch(var(--bc) / 0.1);
    background: oklch(var(--b1));
    box-shadow: 0 18px 34px oklch(var(--bc) / 0.14);
}

.workspace-search-result {
    border-radius: 12px;
}

.workspace-detail-toggle {
    height: 34px;
    padding: 0 10px;
    border-radius: 12px;
    border-color: oklch(var(--bc) / 0.08);
    background: oklch(var(--b2) / 0.58);
}

.workspace-sort {
    flex: 0 0 auto;
}

.workspace-sort__button {
    height: 34px;
    max-width: 220px;
    padding: 0 10px;
    border-radius: 12px;
    border-color: oklch(var(--bc) / 0.08);
    color: oklch(var(--bc) / 0.72);
    background: oklch(var(--b2) / 0.58);
}

.workspace-sort__menu {
    width: 220px;
    padding: 8px;
    border-radius: 16px;
    border-color: oklch(var(--bc) / 0.1);
    background: oklch(var(--b1));
    box-shadow: 0 18px 34px oklch(var(--bc) / 0.14);
}

.workspace-sort__item,
.workspace-sort__clear {
    min-height: 34px;
    border-radius: 12px;
}

.workspace-filter {
    width: 92px;
    height: 34px;
    padding: 0 24px 0 10px;
    border-radius: 12px;
    border: 1px solid oklch(var(--bc) / 0.08);
    color: oklch(var(--bc) / 0.72);
    background: oklch(var(--b2) / 0.58);
}

.workspace-content {
    position: relative;
    display: flex;
    min-width: 0;
    min-height: 0;
    flex: 1;
    gap: 12px;
    padding: 12px;
    background: linear-gradient(145deg, oklch(var(--b2) / 0.65), oklch(var(--b1) / 0.4));
}

.workspace-pane {
    display: flex;
    min-width: 0;
    min-height: 0;
    flex-direction: column;
    overflow: hidden;
    border: 1px solid oklch(var(--bc) / 0.08);
    border-radius: 16px;
    background: color-mix(in oklch, oklch(var(--b1)) 92%, transparent);
    box-shadow: inset 0 1px 0 oklch(var(--b1) / 0.45);
}

.workspace-pane__header {
    height: 40px;
    flex: 0 0 40px;
    padding: 0 14px;
    border-bottom: 1px solid oklch(var(--bc) / 0.08);
}

.workspace-pane__hint {
    color: oklch(var(--bc) / 0.46);
}

.workspace-pane__body {
    min-height: 0;
    flex: 1;
}

.workspace-pane--tree {
    width: 320px;
    flex: 0 0 auto;
    background: linear-gradient(180deg, oklch(var(--b2) / 0.76), oklch(var(--b1) / 0.92));
}

.workspace-pane--resources {
    flex: 1 1 520px;
}

.workspace-pane--actions {
    position: absolute;
    z-index: 81;
    left: 14px;
    right: 14px;
    bottom: 14px;
    width: min(760px, calc(100% - 28px));
    height: fit-content;
    max-height: calc(100% - 28px);
    overflow: visible;
    margin: 0 auto;
    border-radius: 16px;
    border: 1px solid oklch(var(--bc) / 0.08);
    background: color-mix(in oklch, oklch(var(--b1)) 92%, transparent);
    box-shadow: 0 20px 40px oklch(var(--bc) / 0.14);
    transition: width 200ms ease;
}

.workspace-pane__body--actions {
    flex: 0 0 auto;
    min-height: 0;
    overflow: visible;
    padding: 6px 6px 5px;
}

.workspace-pane__body--resources {
    position: relative;
}

.workspace-empty-art {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    padding: clamp(20px, 4vw, 52px);
    pointer-events: none;
}

.batch-drawer-backdrop {
    position: absolute;
    z-index: 2;
    inset: 12px;
    display: none;
    border-radius: 16px;
    background: oklch(var(--bc) / 0.16);
}

.selection-action-bar {
    position: absolute;
    z-index: 4;
    bottom: 18px;
    left: 50%;
    display: flex;
    min-height: 50px;
    max-width: calc(100% - 32px);
    align-items: center;
    gap: 18px;
    padding: 8px 10px 8px 14px;
    border: 1px solid oklch(var(--p) / 0.18);
    border-radius: 16px;
    color: oklch(var(--pc));
    background: color-mix(in oklch, oklch(var(--p)) 92%, oklch(var(--b1)) 8%);
    box-shadow: 0 18px 34px oklch(var(--bc) / 0.14);
    transform: translateX(-50%);
}

.selection-action-bar__summary strong {
    font-size: 13px;
}

.selection-action-bar__summary span {
    opacity: 0.78;
}

.selection-action-bar .btn-primary {
    border-color: oklch(var(--pc) / 0.22);
    color: oklch(var(--p));
    background: oklch(var(--pc));
}

.selection-action-bar .btn-ghost {
    color: oklch(var(--pc));
}

.resize-handle {
    width: 1px;
    flex: 0 0 1px;
    background-color: oklch(var(--bc) / 0.1);
    cursor: col-resize;
    transition: background-color 160ms ease;
    position: relative;
}

.resize-handle::after {
    content: '';
    position: absolute;
    top: 0;
    left: -5px;
    width: 11px;
    height: 100%;
    background-color: transparent;
}

.resize-handle:hover {
    background-color: oklch(var(--p) / 0.8);
}

/* 确保拖动时拖动条保持显示 */
.resize-handle.resizing,
.resize-handle:hover {
    background-color: oklch(var(--p) / 0.8);
}

@media (max-width: 768px) {
    .workspace-shell {
        height: auto;
        min-height: calc(100dvh - 60px);
        padding: 10px;
    }

    .workspace-window {
        min-height: calc(100dvh - 60px);
        border-radius: 18px;
    }

    .workspace-hero {
        grid-template-columns: 1fr;
        gap: 12px;
        padding: 16px;
    }

    .workspace-hero h1 {
        max-width: none;
        font-size: 30px;
    }

    .workspace-hero__stats {
        grid-template-columns: 1fr;
    }

    .workspace-toolbar {
        min-height: 0;
        flex: 0 0 auto;
        padding: 12px;
        flex-wrap: wrap;
    }

    .workspace-toolbar__path {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        gap: 8px;
        width: 100%;
        overflow: visible;
    }

    .workspace-toolbar__path-main {
        width: 100%;
    }

    .workspace-toolbar__controls {
        width: 100%;
        margin-left: 0;
        flex-wrap: wrap;
    }

    .workspace-filter {
        width: 92px;
    }

    .workspace-refresh-button {
        flex: 0 0 auto;
    }

    .workspace-sort {
        flex: 0 0 auto;
    }

    .workspace-sort__button {
        max-width: 170px;
    }

    .workspace-breadcrumb {
        max-width: 120px;
    }

    .workspace-content {
        display: block;
        padding: 10px;
    }

    .workspace-pane {
        width: 100%;
        min-height: 0;
    }

    .workspace-pane--tree {
        width: 100%;
    }

    .workspace-pane--actions {
        top: auto;
        left: 10px;
        right: 10px;
        bottom: 10px;
        width: calc(100% - 20px);
        height: fit-content;
        max-height: calc(100% - 20px);
    }

    .workspace-pane--actions.is-expanded {
        width: calc(100% - 20px);
    }

    .workspace-pane__body--actions {
        overflow-y: auto;
    }

    .batch-drawer-backdrop {
        display: block;
        inset: 10px;
    }

    .selection-action-bar {
        bottom: 12px;
        width: calc(100% - 24px);
        justify-content: space-between;
        gap: 8px;
        padding: 8px 12px;
    }

    .selection-action-bar__summary span {
        display: none;
    }

    .workspace-pane__body--actions {
        padding: 0;
    }

    .resize-handle {
        display: none;
    }
}
</style>
